import { Card, Icon, Paper, InputBase, IconButton, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSelect from "components/MDSelect/MDSelect";
import MDAlert from "components/MDAlert";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Opportunity, OrganizationOpportunityStatus, UserPermissions } from "generated/graphql";
import useGetOpportunities from "hooks/opportunities/useGetOpportunities";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import useGetAllOrganizationUsersNonEmployee from "hooks/users/useGetAllOrganizationUsersNonEmployee";
import useUpdateOpportunityStatus from "hooks/opportunities/useUpdateOpportunityStatus";
import { useBitumioContext } from "providers/BitumioContext";
import useGlobalMessage from "hooks/notifications/useGlobalMessage";
import { useCheckPermissions } from "hooks/authentication/useCheckPermissions";
import { capitalCase } from "change-case";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import OpportunityEditModal from "modules/Opportunities/OpportunityEditModal";

const Search = ({ defaultValue, onQueryChange }) => {
  return (
    <MDBox position="relative" zIndex={2} width="100%">
      <Paper
        component="form"
        sx={{ p: "2px 4px", display: "flex", alignItems: "center", width: "100%" }}
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <InputBase
          sx={{ ml: 1, flex: 1, fontSize: "14px" }}
          placeholder="Search Opportunities"
          value={defaultValue}
          inputProps={{ "aria-label": "search opportunities" }}
          onChange={({ currentTarget }: any) => {
            onQueryChange(currentTarget.value);
          }}
        />
        <IconButton type="button" sx={{ p: "10px" }} aria-label="search">
          <Icon>search</Icon>
        </IconButton>
      </Paper>
    </MDBox>
  );
};

interface OpportunityKanbanProps {
  statuses: OrganizationOpportunityStatus[] | null;
  viewMode: "table" | "kanban";
  onViewModeChange: (nextMode: "table" | "kanban") => void;
}

export default function OpportunityKanban({
  statuses,
  viewMode,
  onViewModeChange,
}: OpportunityKanbanProps) {
  const [{ signedInUser }] = useBitumioContext();
  const { hasPermissions } = useCheckPermissions();
  const { show } = useGlobalMessage();
  const canViewAll = hasPermissions(UserPermissions.VIEW_OPPORTUNITIES);
  const isAssignedOnly =
    !canViewAll && hasPermissions(UserPermissions.VIEW_ONLY_ASSIGNED_OPPORTUNITIES);
  const filtersStorageKey = `opportunitiesKanbanFilters:v2:${signedInUser?.id ?? "anonymous"}`;

  const savedFilters = JSON.parse(
    localStorage.getItem(filtersStorageKey) || "null"
  ) as { searchTerm?: string; userId?: string } | null;

  const { filtering, offset } = usePaginatedVariables({
    initialSearchTerm: savedFilters?.searchTerm ?? null,
    initialUserID: isAssignedOnly ? signedInUser?.id ?? null : null,
    initialPageSize: 500,
  });

  useEffect(() => {
    if (!isAssignedOnly) {
      return;
    }

    if (filtering.userID !== (signedInUser?.id ?? null)) {
      filtering.setUserID(signedInUser?.id ?? null);
    }
  }, [filtering.setUserID, filtering.userID, isAssignedOnly, signedInUser?.id]);

  useEffect(() => {
    localStorage.setItem(
      filtersStorageKey,
      JSON.stringify({
        searchTerm: filtering.searchTerm,
        userId: filtering.userID,
      })
    );
  }, [filtersStorageKey, filtering.searchTerm, filtering.userID]);

  const params = {
    page: offset.page,
    first: offset.first,
    search: filtering.debouncedSearchTerm,
    userId: isAssignedOnly ? signedInUser?.id ?? null : filtering.userID,
  };

  const { data, loading, error, refetch } = useGetOpportunities(params);
  const [updateOpportunityStatus] = useUpdateOpportunityStatus();
  const { data: organizationUsersData, error: organizationUsersError } =
    useGetAllOrganizationUsersNonEmployee();

  const [localOpportunities, setLocalOpportunities] = useState<Opportunity[]>([]);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  useEffect(() => {
    setLocalOpportunities((data ?? []) as Opportunity[]);
  }, [data]);

  const orderedStatuses = useMemo(() => {
    return (statuses ?? []).slice().sort((a, b) => a.ordering - b.ordering);
  }, [statuses]);

  const statusById = useMemo(() => {
    return new Map(orderedStatuses.map((status) => [status.id, status]));
  }, [orderedStatuses]);

  const opportunitiesByStatus = useMemo(() => {
    const grouped: Record<string, Opportunity[]> = {};
    orderedStatuses.forEach((status) => {
      grouped[status.id] = [];
    });

    localOpportunities.forEach((opportunity) => {
      const statusId = opportunity.statusId as string;
      if (!grouped[statusId]) {
        grouped[statusId] = [];
      }
      grouped[statusId].push(opportunity);
    });

    return grouped;
  }, [localOpportunities, orderedStatuses]);

  const handleDragEnd = useCallback(
    async (result) => {
      const { destination, source, draggableId } = result;

      if (!destination) {
        return;
      }

      if (destination.droppableId === source.droppableId) {
        return;
      }

      const destinationStatus = statusById.get(destination.droppableId);
      if (!destinationStatus) {
        return;
      }

      setLocalOpportunities((prev) =>
        prev.map((opportunity) =>
          opportunity.id === draggableId
            ? {
                ...opportunity,
                statusId: destinationStatus.id,
                status: destinationStatus.name,
              }
            : opportunity
        )
      );

      try {
        await updateOpportunityStatus({ id: draggableId, statusId: destinationStatus.id });
      } catch (err) {
        show({ message: "Unable to update opportunity status.", options: { variant: "error" } });
        refetch();
      }
    },
    [refetch, show, statusById, updateOpportunityStatus]
  );

  const userFilterOptions = useMemo(() => {
    const base = [{ label: "All Users", value: "" }];
    const users =
      organizationUsersData?.map((user) => ({
        label: user.name,
        value: user.id,
      })) ?? [];
    return base.concat(users);
  }, [organizationUsersData]);

  const canDrag = hasPermissions(UserPermissions.MANAGE_OPPORTUNITIES);
  const handleCardClick = useCallback((opportunityId: string) => {
    setSelectedOpportunityId(opportunityId);
    setEditModalOpen(true);
  }, []);

  return (
    <>
      <MDBox mb={3} display="flex" gap={2} flexWrap="wrap" alignItems="center" justifyContent="space-between">
        <MDBox flex="1 1 50%" minWidth="280px" maxWidth="50%">
          <Search defaultValue={filtering.searchTerm} onQueryChange={filtering.setSearchTerm} />
        </MDBox>
        <MDBox display="flex" alignItems="center" gap={2} flexWrap="nowrap" justifyContent="flex-end">
          {!isAssignedOnly && (
            <MDBox flex="0 0 260px">
              <MDSelect
                fullWidth
                label="Assigned User"
                name="opportunityUser"
                value={filtering.userID ?? ""}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  filtering.setUserID(nextValue ? String(nextValue) : null);
                }}
                options={userFilterOptions}
              />
            </MDBox>
          )}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(_, nextValue) => {
              if (nextValue) {
                onViewModeChange(nextValue);
              }
            }}
          >
            <ToggleButton value="table" aria-label="Table view">
              <Icon fontSize="small">view_list</Icon>
            </ToggleButton>
            <ToggleButton value="kanban" aria-label="Kanban view">
              <Icon fontSize="small">view_column</Icon>
            </ToggleButton>
          </ToggleButtonGroup>
        </MDBox>
      </MDBox>

      {(loading || error || organizationUsersError) && (
        <div style={{ padding: "16px" }}>
          {error && <MDAlert color="error">{error.message}</MDAlert>}
          {organizationUsersError && (
            <MDAlert color="error">{organizationUsersError.message}</MDAlert>
          )}
        </div>
      )}

      {!loading && !error && orderedStatuses.length === 0 && (
        <MDAlert color="info">No opportunity statuses found.</MDAlert>
      )}

      {!loading && !error && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <MDBox
            sx={{
              display: "flex",
              gap: 1.25,
              overflowX: "auto",
              paddingBottom: 2,
            }}
          >
            {orderedStatuses.map((status) => (
              <Droppable key={status.id} droppableId={status.id}>
                {(provided) => (
                  <MDBox
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      minWidth: 220,
                      maxWidth: 240,
                      backgroundColor: "#f7f7f9",
                      borderRadius: "12px",
                      padding: 1.5,
                      flexShrink: 0,
                    }}
                  >
                    <MDBox display="flex" alignItems="center" justifyContent="space-between">
                      <MDTypography variant="button" fontWeight="medium" sx={{ fontSize: "0.8rem" }}>
                        {capitalCase(status.name)}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" sx={{ fontSize: "0.7rem" }}>
                        {opportunitiesByStatus[status.id]?.length ?? 0}
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1.25, fontSize: "0.7rem" }}>
                      {formatCentsToUSD(
                        (opportunitiesByStatus[status.id] ?? []).reduce(
                          (sum, opportunity) =>
                            sum + (opportunity.estimatedOpportunityValue ?? 0),
                          0
                        )
                      )}
                    </MDTypography>

                    {opportunitiesByStatus[status.id]?.map((opportunity, index) => (
                      <Draggable
                        key={opportunity.id}
                        draggableId={String(opportunity.id)}
                        index={index}
                        isDragDisabled={!canDrag}
                      >
                        {(draggableProvided) => (
                          <Card
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            {...draggableProvided.dragHandleProps}
                            onClick={() => handleCardClick(String(opportunity.id))}
                            role="button"
                            tabIndex={0}
                            sx={{
                              padding: "8px",
                              marginBottom: "8px",
                              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                              cursor: "pointer",
                            }}
                          >
                            <MDTypography variant="button" fontWeight="medium" sx={{ fontSize: "0.78rem" }}>
                              {opportunity.name}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" sx={{ fontSize: "0.7rem" }}>
                              {opportunity.company?.name ?? "—"}
                            </MDTypography>
                            <MDBox display="flex" justifyContent="space-between" mt={0.5}>
                              <MDTypography variant="caption" color="text" sx={{ fontSize: "0.7rem" }}>
                                {formatCentsToUSD(opportunity.estimatedOpportunityValue ?? 0)}
                              </MDTypography>
                              <MDTypography variant="caption" color="text" sx={{ fontSize: "0.7rem" }}>
                                {opportunity.user
                                  ? `${opportunity.user.firstName} ${opportunity.user.lastName}`
                                  : "Unassigned"}
                              </MDTypography>
                            </MDBox>
                          </Card>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </MDBox>
                )}
              </Droppable>
            ))}
          </MDBox>
        </DragDropContext>
      )}
      <OpportunityEditModal
        open={editModalOpen}
        opportunityId={selectedOpportunityId}
        onClose={() => setEditModalOpen(false)}
      />
    </>
  );
}
